// @flow

import * as React from 'react';
import Box from 'sail/Box';
import {Text, Heading, Body, BodyAlt} from 'sail/Text';
import type {VideoModelWithTopic} from 'docs/data';
import ContentList, {ContentListItem} from 'sail/ContentList';
import Icon from 'sail/Icon';
import type {VideoPlaylist} from './data/types';
import {
  getTopicID,
  convertTimeToString,
  polarToCartesian,
  SECONDS_LEFT_TO_CONSIDER_WATCHED,
} from './data/util';

type PlaylistVideoSelectorProps = {|
  videosList: VideoPlaylist,
  currentVideo: VideoModelWithTopic,
  setCurrentTopic: (string) => void,
  playlistTitle: string,
  playlistID: string,
  isCurrentVideoPlaying: boolean,
  onPlayClick: () => void,
|};

const DurationIcon = ({size, percent}) => {
  const centerX = 8;
  const centerY = 8;
  const radius = size / 2 - 1;
  const startAngle = 0;
  const endAngle = Math.floor(percent * 360);
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  const d = [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(' ');

  return (
    <svg className="PlaylistVideoSelector-in-progress-icon">
      <path id="arc" fill="none" d={d} strokeWidth="2" />
    </svg>
  );
};

const VideoStatus = ({
  hasWatched,
  videoDuration,
  playlistIndex,
  isSelected,
  secondsWatched,
  isPlaying,
  onPlayClick,
}) => {
  const percentComplete = secondsWatched / videoDuration;
  let content = null;

  if (isSelected) {
    content = (
      <Icon
        icon={isPlaying ? 'pause' : 'play'}
        onClick={onPlayClick}
        hoverable
        color="gray"
        size={16}
      />
    );
  } else if (hasWatched) {
    content = <Icon icon="checkmark" color="green" size={16} />;
  } else if (secondsWatched > 3) {
    content = (
      <DurationIcon size={16} percent={Math.max(percentComplete, 0.25)} />
    );
  } else {
    content = <Text color="gray">{playlistIndex}</Text>;
  }

  return (
    <Box
      flex={{justifyContent: 'center', alignItems: 'center'}}
      margin={{right: 4}}
      style={{width: 16}}
    >
      {content}
    </Box>
  );
};

const VideoTitle = ({hasWatched, isSelected, title}) => {
  // This is very brittle. We should update the data model so
  // we can avoid this:
  const normalizedTitle =
    title.lastIndexOf(' with') > 0
      ? title.substring(0, title.lastIndexOf(' with'))
      : title;

  if (isSelected) {
    return <Body color="dark">{normalizedTitle}</Body>;
  } else if (hasWatched) {
    return <BodyAlt color="gray">{normalizedTitle}</BodyAlt>;
  } else {
    return <BodyAlt color="blue">{normalizedTitle}</BodyAlt>;
  }
};

export const PlaylistVideoSelector = ({
  videosList,
  setCurrentTopic,
  currentVideo,
  playlistTitle,
  playlistID,
  isCurrentVideoPlaying,
  onPlayClick,
}: PlaylistVideoSelectorProps) => {
  const selectedIndex =
    videosList.findIndex((video) => video.current === true) || 0;

  const playlistRef = React.useRef<?HTMLElement>(null);

  React.useEffect(() => {
    // Scroll the current selected into view on initial load. This is needed
    // since the video list is scrollable and the current video might be
    // last.
    if (playlistRef.current) {
      const list = playlistRef.current.querySelector(
        '.PlaylistVideoSelector-list',
      );
      if (list) {
        const selected = list.querySelector(
          '.PlaylistVideoSelector-selected-item',
        );

        if (selected) {
          list.scrollTop =
            selected.offsetTop -
            list.offsetTop -
            list.getBoundingClientRect().height / 2;
        }
      }
    }
  }, [playlistID]);

  return (
    <div className="PlaylistVideoSelector" ref={playlistRef}>
      <Box
        padding={{horizontal: 20, vertical: 16}}
        flex={{justifyContent: 'space-between', direction: 'column'}}
        divider={{color: 'light', width: 1, side: 'bottom'}}
      >
        <Box padding={{bottom: 4}}>
          <Heading>{playlistTitle}</Heading>
        </Box>
        <Text color="gray">
          {selectedIndex + 1} / {videosList.length} tutorials
        </Text>
      </Box>
      <ContentList divider="none" className="PlaylistVideoSelector-list">
        {videosList.map(({current, secondsWatched, video}, index) => {
          const timeLeft = video.duration - secondsWatched;
          const hasWatched = timeLeft < SECONDS_LEFT_TO_CONSIDER_WATCHED;

          return (
            <ContentListItem
              key={video.id}
              divider={false}
              background={current ? 'offset' : 'white'}
              className={
                current ? 'PlaylistVideoSelector-selected-item' : undefined
              }
              start={
                <VideoStatus
                  hasWatched={hasWatched}
                  playlistIndex={index + 1}
                  secondsWatched={secondsWatched}
                  isPlaying={current && isCurrentVideoPlaying}
                  onPlayClick={onPlayClick}
                  isSelected={current}
                  videoDuration={video.duration}
                />
              }
              title={
                <VideoTitle
                  isSelected={current}
                  hasWatched={hasWatched}
                  title={video.title}
                />
              }
              end={
                <Text color={'gray'}>
                  {convertTimeToString(video.duration)}
                </Text>
              }
              startAlign={'center'}
              onClick={() => setCurrentTopic(getTopicID(video))}
            />
          );
        })}
      </ContentList>
    </div>
  );
};
